/**
 * LegalQuotePanel - Display full legal text in context panel
 *
 * Clean, minimal display of law text with source information.
 */

import React from 'react';
import { ExternalLink, Scale, FileText, Info } from 'lucide-react';
import { ContextPanel } from '../ContextPanel';
import type { LegalReference } from '../../types';
import './LegalQuotePanel.css';

export interface LegalQuotePanelProps {
  isOpen: boolean;
  onClose: () => void;
  legalRef: LegalReference | null;
  className?: string;
}

export const LegalQuotePanel: React.FC<LegalQuotePanelProps> = ({
  isOpen,
  onClose,
  legalRef,
  className = '',
}) => {
  if (!legalRef) return null;

  return (
    <ContextPanel
      isOpen={isOpen}
      onClose={onClose}
      title={legalRef.shortCitation}
      subtitle={legalRef.lawFullName}
      width="medium"
      className={`legal-quote-panel ${className}`}
    >
      <div className="legal-quote-panel__content">
        {/* Quote section */}
        <section className="legal-quote-panel__section legal-quote-panel__quote-section">
          <div className="legal-quote-panel__section-header">
            <Scale className="legal-quote-panel__section-icon" aria-hidden="true" />
            <h3 className="legal-quote-panel__section-title">Gesetzestext</h3>
          </div>
          <blockquote className="legal-quote-panel__quote">{legalRef.quote}</blockquote>
        </section>

        {/* Source info */}
        <section className="legal-quote-panel__section">
          <div className="legal-quote-panel__section-header">
            <FileText className="legal-quote-panel__section-icon" aria-hidden="true" />
            <h3 className="legal-quote-panel__section-title">Quelle</h3>
          </div>
          <dl className="legal-quote-panel__source-list">
            <div className="legal-quote-panel__source-item">
              <dt>Paragraph</dt>
              <dd>{legalRef.title}</dd>
            </div>
            <div className="legal-quote-panel__source-item">
              <dt>Inkrafttreten</dt>
              <dd>{legalRef.effectiveDate}</dd>
            </div>
            {legalRef.lastAmended && (
              <div className="legal-quote-panel__source-item">
                <dt>Letzte Änderung</dt>
                <dd>{legalRef.lastAmended}</dd>
              </div>
            )}
          </dl>
          {legalRef.sourceUrl && (
            <a
              href={legalRef.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="legal-quote-panel__external-link"
            >
              <ExternalLink className="legal-quote-panel__external-icon" aria-hidden="true" />
              Volltext
            </a>
          )}
        </section>

        {/* Relevance explanation */}
        {legalRef.relevanceExplanation && (
          <section className="legal-quote-panel__section">
            <div className="legal-quote-panel__section-header">
              <Info className="legal-quote-panel__section-icon" aria-hidden="true" />
              <h3 className="legal-quote-panel__section-title">Bedeutung für deinen Betrieb</h3>
            </div>
            <p className="legal-quote-panel__relevance-text">{legalRef.relevanceExplanation}</p>
          </section>
        )}
      </div>
    </ContextPanel>
  );
};

export default LegalQuotePanel;
